/**
 * The one place the UI gets its engine. Hosts:
 *  - desktop: the preload bridge exposes `window.hub.engine` (Electron main hosts the engine on node:sqlite)
 *  - extension side panel: the background service worker hosts the engine; `window.hub.engine` is provided by
 *    apps/extension's panel bootstrap before the SPA mounts
 *  - web: a Web Worker running @hub/engine with the sql.js driver (see ./worker-transport.ts)
 */
import type { EngineNotification, EngineTransport } from "@hub/shared";

export interface HubHostBridge {
  engine: EngineTransport;
  platform: "darwin" | "win32" | "linux" | "web" | "extension";
  mode: "desktop" | "extension" | "web";
  /** Desktop only. */
  integrations?: HostIntegrations;
  shell?: {
    openExternal(url: string): Promise<void>;
    showNotification(title: string, body: string, data?: Record<string, unknown>): Promise<void>;
    setBadge(count: number): Promise<void>;
    getAppIcons(): Promise<{ id: string; name: string; preview: string }[]>;
    setAppIcon(id: string | null): Promise<void>;
    setThemeBackground(color: string, dark: boolean): Promise<void>;
    getVersion(): Promise<string>;
    pickFile(opts: { extensions: string[] }): Promise<{ name: string; content: string } | null>;
    saveFile(opts: { defaultName: string; content: string }): Promise<boolean>;
    onDeepLink(cb: (url: string) => void): () => void;
    /** Desktop only: native menu items (newPerson, search, commandBar, toggleSidebar, syncNow, settings, sendFeedback). */
    onMenuAction?(cb: (action: string) => void): () => void;
    /** Desktop only: a CSV / LinkedIn CSV picked through a host integration, handed to the import flow. */
    onPickedFile?(cb: (file: { integration: HostIntegrationId; name: string; content: string }) => void): () => void;
  };
  auth?: {
    getToken(): Promise<string | null>;
    setToken(token: string | null): Promise<void>;
  };
}

export type IntegrationProviderId = "google" | "microsoft";
export type KnownHostIntegrationId =
  | "apple_contacts"
  | "apple_messages"
  | "apple_calls"
  | "apple_calendar"
  | "apple_mail"
  | "google"
  | "microsoft"
  | "csv"
  | "linkedin_csv"
  | "ics";
/** Accounts of an OAuth provider are their own integrations, `google:acct-…`. */
export type HostIntegrationId = KnownHostIntegrationId | `${IntegrationProviderId}:${string}`;

export function parseAccountIntegrationId(id: string): { provider: IntegrationProviderId; accountId: string } | null {
  const m = /^(google|microsoft):([A-Za-z0-9_-]{1,64})$/.exec(id);
  return m ? { provider: m[1] as IntegrationProviderId, accountId: m[2] } : null;
}

export interface HostIntegrationStatus {
  id: HostIntegrationId;
  available: boolean; // supported on this platform
  authorized: boolean; // permission granted
  requiresDiskAccess: boolean;
  hasDiskAccess: boolean;
  lastSync: string | null;
  syncing: boolean;
  error: string | null;
  accountLabel: string | null;
  configured?: boolean;
  provider?: IntegrationProviderId;
  accountCount?: number;
  calendars?: { id: string; name: string; enabled: boolean }[];
}

export interface HostIntegrations {
  list(): Promise<HostIntegrationStatus[]>;
  authorize(id: HostIntegrationId, opts?: Record<string, unknown>): Promise<HostIntegrationStatus>;
  openDiskAccessSettings(): Promise<void>;
  sync(id: HostIntegrationId, opts?: { full?: boolean }): Promise<HostIntegrationStatus>;
  remove(id: HostIntegrationId, deleteData: boolean): Promise<void>;
  setOptions?(id: HostIntegrationId, opts: Record<string, unknown>): Promise<HostIntegrationStatus>;
  onStatus(cb: (status: HostIntegrationStatus) => void): () => void;
}

declare global {
  interface Window {
    hub?: HubHostBridge;
  }
}

let cached: Promise<EngineTransport> | null = null;
let workerFactory: (() => Promise<EngineTransport>) | null = null;

/** Web mode registers how to create the worker-backed engine (keeps sql.js out of desktop and extension bundles). */
export function registerWorkerEngineFactory(factory: () => Promise<EngineTransport>) {
  workerFactory = factory;
}

export function getEngine(): Promise<EngineTransport> {
  if (cached) return cached;
  if (typeof window !== "undefined" && window.hub?.engine) {
    cached = Promise.resolve(window.hub.engine);
    return cached;
  }
  if (!workerFactory) return Promise.reject(new Error("No engine host available: register a worker engine factory for web mode"));
  // Cache the promise, not the result, so concurrent first calls share one engine.
  cached = workerFactory().catch((e) => {
    cached = null;
    throw e;
  });
  return cached;
}

export function getHost(): HubHostBridge | null {
  return typeof window !== "undefined" ? window.hub ?? null : null;
}

export function hostMode(): "desktop" | "extension" | "web" {
  return getHost()?.mode ?? (typeof __HUB_MODE__ !== "undefined" ? __HUB_MODE__ : "web");
}

export function subscribeEngine(listener: (n: EngineNotification) => void): () => void {
  let unsub: (() => void) | null = null;
  let cancelled = false;
  getEngine().then((e) => {
    if (cancelled) return;
    unsub = e.subscribe(listener);
  });
  return () => {
    cancelled = true;
    unsub?.();
  };
}
